import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import QuizRoundedIcon from '@mui/icons-material/QuizRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import StudentLayout from './components/StudentLayout';
import { AuthContext } from '../auth/AuthContext';
import { listAssessments } from '../api/assessments';
import { createSubmission, listSubmissions } from '../api/submissions';

const Assessments = () => {
  const { user } = useContext(AuthContext) ?? {};
  const [assessments, setAssessments] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [error, setError] = useState(null);

  const grade = user?.grade;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [assessmentData, submissionData] = await Promise.all([
        listAssessments({ grade }),
        listSubmissions({}),
      ]);
      setAssessments(Array.isArray(assessmentData) ? assessmentData : []);
      const submissionMap = {};
      (submissionData || []).forEach((sub) => { submissionMap[sub.assessmentId] = sub; });
      setSubmissions(submissionMap);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [grade]); // intentionally depends on grade only

  const handleSubmit = async (assessmentId, files) => {
    if (!files?.length) return;
    setUploadingId(assessmentId);
    setError(null);
    try {
      await createSubmission({ assessmentId, files });
      const submissionData = await listSubmissions({});
      const submissionMap = {};
      (submissionData || []).forEach((sub) => { submissionMap[sub.assessmentId] = sub; });
      setSubmissions(submissionMap);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to submit assessment');
    } finally {
      setUploadingId(null);
    }
  };

  const summary = useMemo(() => {
    const total = assessments.length;
    const submitted = assessments.filter((a) => submissions[a._id]).length;
    const graded = assessments.filter((a) => {
      const sub = submissions[a._id];
      return sub && (sub.educatorScore != null || sub.feedback);
    }).length;
    const pct = total ? Math.round((submitted / total) * 100) : 0;
    return { total, submitted, graded, pct };
  }, [assessments, submissions]);

  return (
    <StudentLayout>
      <Box>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary.main">
              Assessments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upcoming tasks, tests, and exams. Submit your work and view scores/feedback.
            </Typography>
          </Box>

          {loading && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}

          <Card variant="outlined">
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <Typography fontWeight={700}>Progress</Typography>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress variant="determinate" value={summary.pct} sx={{ height: 10, borderRadius: 5 }} />
                </Box>
                <Chip label={`${summary.submitted}/${summary.total} submitted`} />
                <Chip label={`${summary.graded} graded`} color="success" />
              </Stack>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            {assessments.length === 0 && !loading && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  No assessments available right now.
                </Typography>
              </Grid>
            )}
            {assessments.map((assessment) => {
              const existing = submissions[assessment._id];
              const isOpen = !assessment.openDate || new Date(assessment.openDate).getTime() <= Date.now();
              const lockAfterDue = assessment.lockAfterDueDate && assessment.dueDate
                ? new Date(assessment.dueDate).getTime() < Date.now()
                : false;
              const isClosed = (assessment.closeDate && new Date(assessment.closeDate).getTime() < Date.now()) || lockAfterDue;

              return (
                <Grid item xs={12} md={6} key={assessment._id}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardHeader
                      title={assessment.title}
                      subheader={assessment.type ? assessment.type.toUpperCase() : 'Assessment'}
                      action={<Chip size="small" label={`Marks: ${assessment.totalMarks || 0}`} />}
                    />
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Typography variant="body2" color="text.secondary">
                          {assessment.description || 'No description provided.'}
                        </Typography>
                        <Stack direction="row" spacing={1} divider={<Divider flexItem orientation="vertical" />}>
                          <Typography variant="caption">
                            Opens: {assessment.openDate ? new Date(assessment.openDate).toLocaleString() : 'Now'}
                          </Typography>
                          <Typography variant="caption">
                            Due: {assessment.dueDate ? new Date(assessment.dueDate).toLocaleString() : 'TBC'}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {assessment.attachments?.map((file) => (
                            <Button
                              key={file.key}
                              startIcon={<DownloadRoundedIcon />}
                              size="small"
                              href={file.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {file.originalName}
                            </Button>
                          ))}
                          {assessment.quizConfig?.url && (
                            <Button
                              startIcon={<QuizRoundedIcon />}
                              size="small"
                              href={assessment.quizConfig.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Attempt Quiz
                            </Button>
                          )}
                        </Stack>

                        {existing ? (
                          <Alert severity="success">
                            Submitted. Status: {existing.status}.
                            {existing.educatorScore != null && ` Score: ${existing.educatorScore}/${existing.totalMarks || assessment.totalMarks || ''}.`}
                            {existing.feedback ? ` Feedback: ${existing.feedback}` : ''}
                            {existing.files?.length > 0 && (
                              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {existing.files.map((f) => (
                                  <Button
                                    key={f.key || f.url}
                                    startIcon={<DownloadRoundedIcon />}
                                    size="small"
                                    href={f.url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {f.originalName || 'Marked work'}
                                  </Button>
                                ))}
                              </Box>
                            )}
                          </Alert>
                        ) : (
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                            <Button
                              component="label"
                              variant="contained"
                              startIcon={<AssignmentTurnedInRoundedIcon />}
                              disabled={uploadingId === assessment._id || !isOpen || isClosed}
                            >
                              {uploadingId === assessment._id ? 'Uploading...' : 'Upload assignment'}
                              <input
                                hidden
                                type="file"
                                multiple
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => handleSubmit(assessment._id, Array.from(e.target.files || []))}
                              />
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              PDF/Word uploads supported. Quiz links auto-mark.
                            </Typography>
                          </Stack>
                        )}

                        {isClosed && !existing && (
                          <Alert severity="warning" icon={<CheckCircleRoundedIcon />}>
                            This assessment is closed.
                          </Alert>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Stack>
      </Box>
    </StudentLayout>
  );
};

export default Assessments;
